import React, { useState, useContext, useMemo, useEffect, FormEvent } from 'react';
import { Trans, useI18nContext } from '@ecg-marktplaats/js-react-i18n';
import {
  LicensePlateTextField,
  TextLarge,
  PrimaryButton,
  SecondaryButton,
  SuffixedTextField,
  Title4,
  Link,
  Image,
  Icon,
  WarningInpage,
  ErrorInpage,
  TextRegular,
} from '@hz-design-system/web-ui';
import validation from '@ecg-marktplaats/aurora-js-validation';

import { trackGAEvent } from '../../../utils/gaTracking';
import getCarValue, { TCarValueResponse } from '../repositories/getCarValue';
import EnvironmentContext from '../../../contexts/EnvironmentContext';
import BlockSkeleton from '../../FeedsApp/components/Skeleton/BlockSkeleton';
import TextSkeleton from '../../FeedsApp/components/Skeleton/TextSkeleton';

import Classes from './SellBlock.scss';

type TSellBlockProps = {
  withLicensePlateField: boolean;
};

let BasicModal;

const STORAGE_KEY = 'sellBlock_carValue';
const ONE_WEEK_MS = 7 * 24 * 60 * 60 * 1000;

type TStoredCarValue = {
  carValue: TCarValueResponse;
  licensePlate: string;
  mileage: string;
  timestamp: number;
};

const isLocalStorageAvailable = (): boolean => {
  try {
    const testKey = '__storage_test__';
    localStorage.setItem(testKey, testKey);
    localStorage.removeItem(testKey);
    return true;
  } catch {
    return false;
  }
};

const getStoredCarValue = (): (TStoredCarValue & { isExpired: boolean }) | null => {
  if (!isLocalStorageAvailable()) {
    return null;
  }
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (!stored) {
      return null;
    }
    const parsed = JSON.parse(stored);
    const isExpired = Date.now() - parsed.timestamp > ONE_WEEK_MS;
    return { ...parsed, isExpired };
  } catch {
    return null;
  }
};

const setStoredCarValue = (data: Omit<TStoredCarValue, 'timestamp'>) => {
  if (!isLocalStorageAvailable()) {
    return;
  }
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify({ ...data, timestamp: Date.now() }));
  } catch {}
};

const SellBlock = ({ withLicensePlateField }: TSellBlockProps) => {
  const { t } = useI18nContext();
  const { xsrfToken } = useContext(EnvironmentContext);
  const [licensePlate, setLicensePlate] = useState('');
  const [mileage, setMileage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [carValue, setCarValue] = useState<TCarValueResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [licensePlateError, setLicensePlateError] = useState<boolean>(false);
  const [mileageError, setMileageError] = useState<boolean>(false);
  const [isResult, setIsResult] = useState(false);
  const [isDisclaimerOpen, setIsDisclaimerOpen] = useState(false);

  useEffect(() => {
    const refetchCarValue = async (storedLicensePlate: string, storedMileage: string) => {
      try {
        const result = await getCarValue(
          { licensePlate: storedLicensePlate, mileage: parseInt(storedMileage, 10) },
          xsrfToken,
        );
        setCarValue(result);
        setStoredCarValue({ carValue: result, licensePlate: storedLicensePlate, mileage: storedMileage });
      } catch {}
    };

    const stored = getStoredCarValue();
    if (stored) {
      setCarValue(stored.carValue);
      setLicensePlate(stored.licensePlate);
      setMileage(stored.mileage);
      setIsResult(true);

      if (stored.isExpired) {
        refetchCarValue(stored.licensePlate, stored.mileage);
      }
    }
  }, [xsrfToken]);

  const handleOpenDisclaimer = async () => {
    try {
      BasicModal = (await import(/* webpackChunkName: "BasicModal" */ '@hz-design-system/web-ui')).BasicModal;
      setIsDisclaimerOpen(true);
    } catch {}
  };

  const handleBlur = (field) => {
    switch (field) {
      case 'licensePlate':
        setLicensePlateError(false);
        if (!licensePlate) {
          setLicensePlateError(true);
        }
        break;
      case 'mileage':
        setMileageError(false);
        if (!mileage) {
          setMileageError(true);
        }
        break;
      default:
        break;
    }
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    trackGAEvent('Clicked', 'CheckValue');

    if (!licensePlate) {
      setLicensePlateError(true);
    }
    if (!mileage) {
      setMileageError(true);
    }

    if (!licensePlate || !mileage) {
      return;
    }

    trackGAEvent('Submitted', 'CheckValue');
    trackGAEvent('checkSellPriceClicked');
    setIsLoading(true);
    setIsResult(true);
    setError(null);
    setCarValue(null);

    try {
      const result = await getCarValue(
        {
          licensePlate,
          mileage: parseInt(mileage, 10),
        },
        xsrfToken,
      );

      setTimeout(() => {
        setCarValue(result);
        setStoredCarValue({ carValue: result, licensePlate, mileage });
      }, 500);

      if (!carValue?.price) {
        trackGAEvent('NoPriceReturned', 'CheckValue');
      }
    } catch {
      setIsResult(false);
      const licensePlateValidator = validation.licensePlate('NL');
      const validLicensePlate = licensePlateValidator.isValid(licensePlate);
      trackGAEvent('Error', 'CheckValue');
      if (!validLicensePlate) {
        setError(t('cars.sell.check_price.error_license_plate'));
      } else {
        setError(t('cars.sell.check_price.error'));
      }
    } finally {
      setTimeout(() => {
        setIsLoading(false);
      }, 500);
    }
  };

  const handleAdjust = () => {
    setIsResult(false);
    trackGAEvent('Clicked', 'ResetValuation');
    if (isLocalStorageAvailable()) {
      localStorage.removeItem(STORAGE_KEY);
    }
  };

  const placeAdUrl = useMemo(() => {
    const params = new URLSearchParams();
    if (licensePlate) {
      params.set('licensePlate', licensePlate);
    }
    if (mileage) {
      params.set('mileage', mileage);
    }
    const queryString = params.toString();
    return `/plaats/91${queryString ? `?${queryString}` : ''}`;
  }, [licensePlate, mileage]);

  return (
    <div className={Classes.cardContainer}>
      <div className={`${Classes.card} ${isResult ? Classes.isResult : ''}`}>
        <div className={Classes.cardFront}>
          <TextLarge className={Classes.cardTitle} strong utilities={['marginBottomXs']}>
            {t('cars.sell.text')}
          </TextLarge>
          <TextLarge className={Classes.cardTitle} strong utilities={['marginBottomXs']}>
            {t('cars.sell.text_long')}
          </TextLarge>
          <form onSubmit={handleSubmit} className={Classes.form}>
            {withLicensePlateField ? (
              <>
                <LicensePlateTextField
                  id="licensePlate"
                  labelText={t('cars.sell.license_plate.label')}
                  errorText={licensePlateError ? t('cars.sell.error.license_plate') : ''}
                  value={licensePlate}
                  locale="nlnl"
                  countryCode="NL"
                  onChange={(e) => setLicensePlate(e.target.value)}
                  onBlur={() => handleBlur('licensePlate')}
                  ariaLabel={t('cars.sell.license_plate_label')}
                />
                <SuffixedTextField
                  labelText={t('cars.sell.mileage.label')}
                  errorText={mileageError ? t('cars.sell.error.mileage') : ''}
                  suffixText="km"
                  value={mileage}
                  onChange={(e) => setMileage(e.target.value)}
                  onBlur={() => handleBlur('mileage')}
                />
              </>
            ) : (
              <TextLarge>{t('cars.sell.body')}</TextLarge>
            )}

            {error && !isLoading && <ErrorInpage title="Error" description={error} />}

            <div className={Classes.actions}>
              {withLicensePlateField ? (
                <>
                  <SecondaryButton
                    as="a"
                    href={placeAdUrl}
                    className={Classes.action}
                    data-sem="placeAdAction"
                    onClick={() => {
                      trackGAEvent('placeAdButtonClicked', 'block');
                    }}
                  >
                    {t('cars.sell.place_ad_button')}
                  </SecondaryButton>
                  <PrimaryButton type="submit" className={Classes.action}>
                    {t('cars.sell.check_price.button')}
                  </PrimaryButton>
                </>
              ) : (
                <PrimaryButton
                  as="a"
                  href="/plaats/91"
                  className={Classes.action}
                  data-sem="placeAdAction"
                  onClick={() => {
                    trackGAEvent('placeAdButtonClicked', 'block');
                  }}
                >
                  {t('cars.sell.place_ad_button')}
                </PrimaryButton>
              )}
            </div>
          </form>
        </div>

        <div className={Classes.cardBack}>
          {Boolean(isLoading || carValue) && (
            <div className={Classes.carResult}>
              <div className={Classes.carResultHeader}>
                <TextLarge strong>{t('cars.sell.my_car')}</TextLarge>
                <Link onClick={handleAdjust}>{t('cars.sell.adjust')}</Link>
              </div>
              <div className={Classes.carInfo}>
                {isLoading ? (
                  <div className={Classes.carLogo}>
                    <BlockSkeleton className={Classes.skeletonLogo} withAnimation />
                  </div>
                ) : (
                  <>
                    {carValue?.logoUrl && (
                      <div className={Classes.carLogo}>
                        <Image src={carValue.logoUrl} alt={t('cars.sell.logo_alt')} fit />
                      </div>
                    )}
                  </>
                )}
                {isLoading ? (
                  <div className={Classes.carDetails}>
                    <TextSkeleton type="" withAnimation />
                    <TextSkeleton type="short" withAnimation />
                    <TextSkeleton type="regular" withAnimation />
                  </div>
                ) : (
                  <div className={Classes.carDetails}>
                    <>
                      <Title4 as="div">{carValue?.title}</Title4>
                      <TextLarge color="text-secondary">
                        {carValue?.constructionYear} · {parseInt(mileage, 10).toLocaleString('nl-NL')} km
                      </TextLarge>
                    </>
                  </div>
                )}
              </div>
              {isLoading ? (
                <div className={Classes.priceRow}>
                  <TextSkeleton type="" withAnimation />
                </div>
              ) : (
                <>
                  {carValue?.price ? (
                    <div className={Classes.priceRow}>
                      <div className={Classes.priceLabel}>
                        <TextRegular color="text-secondary">{t('cars.sell.check_price.result_label')}</TextRegular>
                        <button type="button" className={Classes.infoButton} onClick={handleOpenDisclaimer}>
                          <Icon name="info" color="text-secondary" />
                        </button>
                      </div>
                      <Title4 as="div">{carValue.price}</Title4>
                    </div>
                  ) : (
                    <WarningInpage
                      icon="alert"
                      title={t('cars.sell.no_price.title')}
                      description={t('cars.sell.no_price.description')}
                    />
                  )}
                </>
              )}
              <PrimaryButton
                as="a"
                href={placeAdUrl}
                className={Classes.sellButton}
                data-sem="placeAdAction"
                onClick={() => {
                  trackGAEvent('placeAdButtonClicked', 'priceAdvice');
                }}
              >
                {t('cars.sell.sell_car_button')}
              </PrimaryButton>
            </div>
          )}
        </div>
      </div>
      {isDisclaimerOpen && (
        <BasicModal
          isBottomSheet
          size="m"
          title={t('cars.sell.disclaimer.title')}
          onClose={() => setIsDisclaimerOpen(false)}
        >
          <Trans
            i18nKey="cars.sell.disclaimer.body"
            paragraph-open={Trans.safeString('<p>')}
            paragraph-close={Trans.safeString('</p>')}
            bold-close={Trans.safeString('</div>')}
            list-open={Trans.safeString('<ul>')}
            list-close={Trans.safeString('</ul>')}
            list-item-open={Trans.safeString('<li>')}
            list-item-close={Trans.safeString('</li>')}
            break={Trans.safeString('<br>')}
          />
        </BasicModal>
      )}
    </div>
  );
};

export default SellBlock;
